import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState,
} from '@angular/cdk/layout';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, map } from 'rxjs';
import { Constants } from 'src/app/app.constants';
import { AnalyticsAbstract } from 'src/app/services/analytics/analytics.abstract';

import {
  CardType,
  Redemption,
  StampCard,
  StampEvent,
  UserCard,
  UserReward,
} from '../../models/index';

import {
  AuthService,
  CardService,
  GoogleAnalyticsService,
  InsightsService,
  MerchantService,
  RewardService,
  StorageService,
} from '../../services/index';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UserVoucher } from 'src/app/models/user_voucher';
import { RedeemVoucherDialogComponent } from '../gift-vouchers/redeem-voucher-dialog/redeem-voucher-dialog.component';
import { VoucherService } from 'src/app/services/voucher.service';

interface Insights {
  stampEvents?: string;
  points?: string;
  customers?: string;
  cards?: string;
  rewards?: string;
  redemptions?: string;
  showStamps?: boolean;
  showPoints?: boolean;
  showLocation?: boolean;
}

@Component({
  selector: 'activity',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.css'],
})
export class ActivityComponent {
  headlineImageSize = 30;
  color: ThemePalette = 'primary';
  mode: ProgressSpinnerMode = 'determinate';

  exporting = false;
  title = '';
  subtitle: string = '';
  @ViewChild('stampsTable') stampsTable: any;
  @ViewChild('pointsTable') pointsTable: any;
  @ViewChild('cardsTable') cardsTable: any;
  @ViewChild('rewardsTable') rewardsTable: any;
  @ViewChild('redemptionsTable') redemptionsTable: any;
  @ViewChild('vouchersTable') vouchersTable: any;

  stampPageIndex = 0;
  stampPageSize = 20;
  stampLength = 0;

  pointsPageIndex = 0;
  pointsPageSize = 20;
  pointsLength = 0;

  redemptionPageIndex = 0;
  redemptionPageSize = 20;
  redemptionLength = 0;

  cardsPageIndex = 0;
  cardsPageSize = 20;
  cardsLength = 0;

  rewardsPageIndex = 0;
  rewardsPageSize = 20;
  rewardsLength = 0;

  vouchersPageIndex = 0;
  vouchersPageSize = 20;
  vouchersLength = 0;
  showOnlyNonRedeemed = true;

  recentStamps: StampEvent[] = [];
  recentPoints: StampEvent[] = [];
  redemptions: Redemption[] = [];
  cards: UserCard[] = [];
  rewards: UserReward[] = [];
  vouchers: UserVoucher[] = [];

  insights: Insights = {};

  eventsLoading = false;
  redemptionsLoading = false;
  cardsLoading = false;
  rewardsLoading = false;
  vouchersLoading = false;
  insightsLoading = false;
  funnelLoading = false;
  initialLoading = false;
  customerStatsLoading = false;
  hourlyLoading = false;

  error = false;

  tabs = ['stamps', 'points', 'redemptions', 'cards', 'rewards', 'vouchers'];
  selectedTab = '';
  selectedIndex = 0;
  hasStampCards: boolean | null = null;
  hasPointsCards: boolean | null = null;
  hasVouchers: boolean | null = null;

  scrollTimeout: any;
  suppressPaging: boolean = false;

  pointsRange = '';
  stampsRange = '';
  redemptionsRange = '';
  cardsRange = '';
  rewardsRange = '';
  vouchersRange = '';
  days = '30';
  customerId?: string;
  menuOptions: any[] = [
    {
      title: this.constants.strings.exportAllData,
      icon: 'mark_chat_unread',
    },
    {
      title: this.constants.strings.lapsedCustomerReport,
      icon: 'mark_chat_unread',
    },
  ];

  dateOptions: any[] = [
    { title: 'Last 7 days', value: '7' },
    { title: 'Last 30 days', value: '30' },
    { title: 'Last 3 months', value: '90' },
    { title: 'Last 6 months', value: '180' },
    { title: 'Last 12 months', value: '360' },
  ];

  tab: any;

  showLocation = false;
  canViewCustomers = false;

  constructor(
    public constants: Constants,
    private router: Router,
    private route: ActivatedRoute,
    public authService: AuthService,
    private cardService: CardService,
    private rewardService: RewardService,
    private insightsService: InsightsService,
    public dialog: MatDialog,
    private merchantService: MerchantService,
    private ga: GoogleAnalyticsService,
    private storageService: StorageService,
    private mixpanel: AnalyticsAbstract,
    private breakpointObserver: BreakpointObserver,
    private ps: PermissionsService,
    private voucherService: VoucherService,
  ) {
    this.canViewCustomers = this.ps.permissions.customers.view();

    this.route.queryParams.subscribe((params) => {
      this.tab = params['tab'];
    });
  }

  removeCustomerIdQueryParam(): void {
    const urlTree = this.router.createUrlTree([], {
      queryParams: { ...this.route.snapshot.queryParams },
      queryParamsHandling: 'merge',
      preserveFragment: true,
    });

    delete urlTree.queryParams['customerId'];

    this.router.navigateByUrl(urlTree, { replaceUrl: true });
  }

  ngOnInit() {
    this.initialLoading = true;
    this.getDashboardInsights();
    this.getActivityTabAvailability();
    // this.loadData();
  }

  get visibleTabs(): string[] {
    return this.tabs.filter((tab) => this.isTabVisible(tab));
  }

  private isTabVisible(tab: string): boolean {
    if (tab === 'stamps') {
      return this.hasStampCards !== false;
    }

    if (tab === 'points') {
      return this.hasPointsCards !== false;
    }

    if (tab === 'vouchers') {
      return this.hasVouchers !== false;
    }

    return true;
  }

  private selectTab(tab?: string): void {
    const fallbackTab = this.visibleTabs[0] ?? 'redemptions';
    this.selectedTab =
      tab && this.tabs.includes(tab) && this.isTabVisible(tab)
        ? tab
        : fallbackTab;
    this.syncSelectedIndex();
  }

  private syncSelectedIndex(): void {
    if (!this.isTabVisible(this.selectedTab)) {
      this.selectedTab = this.visibleTabs[0] ?? 'redemptions';
      this.addQueryParams({ tab: this.selectedTab });
    }

    this.selectedIndex = this.visibleTabs.findIndex(
      (tab) => tab === this.selectedTab,
    );

    if (this.selectedIndex < 0) {
      this.selectedIndex = 0;
    }
  }

  getActivityTabAvailability(): void {
    this.cardService.getCards(1000, 0).subscribe({
      next: (res: any) => {
        const cards: StampCard[] = res?.docs ?? [];
        this.hasStampCards = cards.some(
          (card) => card.cardType === CardType.stamp,
        );
        this.hasPointsCards = cards.some(
          (card) =>
            card.cardType === CardType.point ||
            card.cardType === CardType.openEndedPoint,
        );
        this.syncSelectedIndex();
      },
      error: () => {
        this.hasStampCards = true;
        this.hasPointsCards = true;
      },
    });

    this.voucherService.getVouchers(1, 0, true).subscribe({
      next: (res: any) => {
        this.hasVouchers = (res?.totalDocs ?? res?.docs?.length ?? 0) > 0;
        this.syncSelectedIndex();
      },
      error: () => {
        this.hasVouchers = true;
      },
    });
  }

  public isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result: BreakpointState) => {
        return result.matches;
      }),
    );

  loadData(): void {
    if (this.selectedTab === 'stamps') {
      this.getStampEvents(false);
    }
    if (this.selectedTab === 'points') {
      this.getStampEvents(true);
    }
    if (this.selectedTab === 'redemptions') {
      this.getRedemptions();
    }
    if (this.selectedTab === 'cards') {
      this.getUserCards();
    }
    if (this.selectedTab === 'rewards') {
      this.getUserRewards();
    }
    if (this.selectedTab === 'vouchers') {
      this.getUserVouchers();
    }
  }

  get loading(): boolean {
    return (
      this.initialLoading ||
      this.eventsLoading ||
      this.redemptionsLoading ||
      this.exporting ||
      this.cardsLoading ||
      this.rewardsLoading ||
      this.vouchersLoading ||
      this.customerStatsLoading
    );
  }

  getDashboardInsights(): void {
    this.insightsLoading = true;
    this.insightsService
      .getDashboardInsights(true)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.insights = res;

            this.showLocation = this.insights?.showLocation ?? false;

            this.selectTab(this.tab);

            this.loadData();
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(
        () => ((this.insightsLoading = false), (this.initialLoading = false)),
      );
    this.subtitle = this.authService.getNestedUserProperty('merchant', 'name');
  }

  getUserCards(): void {
    this.cardsLoading = true;

    this.merchantService
      .getCustomerCards(this.cardsPageSize, this.cardsPageIndex)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.cardsLength = res.totalDocs;
            this.cards = res.docs;

            this.cardsRange = this.constants.pageCounter(
              this.cardsPageIndex,
              this.cardsPageSize,
              this.cardsLength,
              this.cards?.length,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.cardsLoading = false));
  }

  getUserRewards(): void {
    this.rewardsLoading = true;

    this.merchantService
      .getCustomerRewards(this.rewardsPageSize, this.rewardsPageIndex)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.rewardsLength = res.totalDocs;
            this.rewards = res.docs;

            this.rewardsRange = this.constants.pageCounter(
              this.rewardsPageIndex,
              this.rewardsPageSize,
              this.rewardsLength,
              this.rewards?.length,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.rewardsLoading = false));
  }

  getUserVouchers(): void {
    this.vouchersLoading = true;

    this.merchantService
      .getCustomerVouchers(
        this.vouchersPageSize,
        this.vouchersPageIndex,
        undefined,
        this.showOnlyNonRedeemed,
      )
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.vouchersLength = res.totalDocs;
            this.vouchers = res.docs;

            this.vouchersRange = this.constants.pageCounter(
              this.vouchersPageIndex,
              this.vouchersPageSize,
              this.vouchersLength,
              this.vouchers?.length,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.vouchersLoading = false));
  }

  onNonRedeemedToggleChange(): void {
    this.vouchersPageIndex = 0;
    this.getUserVouchers();
  }

  getStampEvents(points: boolean): void {
    this.eventsLoading = true;
    this.cardService
      .getStampEvents(
        points ? this.pointsPageSize : this.stampPageSize,
        points ? this.pointsPageIndex : this.stampPageIndex,
        undefined,
        undefined,
        points,
      )
      .subscribe({
        next: (res: any) => {
          if (res) {
            if (points) {
              this.pointsLength = res.totalDocs;
              this.recentPoints = res.docs;

              this.pointsRange = this.constants.pageCounter(
                this.pointsPageIndex,
                this.pointsPageSize,
                this.pointsLength,
                this.recentPoints?.length,
              );
            } else {
              this.stampLength = res.totalDocs;
              this.recentStamps = res.docs;

              this.stampsRange = this.constants.pageCounter(
                this.stampPageIndex,
                this.stampPageSize,
                this.stampLength,
                this.recentStamps?.length,
              );
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.eventsLoading = false));
  }

  getRedemptions(): void {
    this.redemptionsLoading = true;
    this.rewardService
      .getRedemptions(this.redemptionPageSize, this.redemptionPageIndex)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.redemptionLength = res.totalDocs;
            this.redemptions = res.docs;

            this.redemptionsRange = this.constants.pageCounter(
              this.redemptionPageIndex,
              this.redemptionPageSize,
              this.redemptionLength,
              this.redemptions?.length,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.redemptionsLoading = false));
  }

  addQueryParams(params: any) {
    this.route.queryParams.subscribe((queryParams) => {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: params,
        queryParamsHandling: 'merge',
        skipLocationChange: false,
      });
    });
  }

  tabChanged(index: any): void {
    let tab = this.visibleTabs[index];

    if (!tab) {
      return;
    }

    this.addQueryParams({ tab: tab });

    this.selectedTab = tab;

    this.ga.event(`db_${tab}`);

    this.mixpanel.track(Constants.analytics_keys.selectDashboardTab, {
      Tab: tab,
    });

    this.loadData();
  }

  cardPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.cardsPageIndex = event.offset;
      this.addQueryParams({ p: this.cardsPageIndex });
      this.getUserCards();
    }
  }

  rewardPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.rewardsPageIndex = event.offset;
      this.addQueryParams({ p: this.rewardsPageIndex });
      this.getUserRewards();
    }
  }

  voucherPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.vouchersPageIndex = event.offset;
      this.addQueryParams({ p: this.vouchersPageIndex });
      this.getUserVouchers();
    }
  }

  stampPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.stampPageIndex = event.offset;
      this.addQueryParams({ p: this.stampPageIndex });
      this.getStampEvents(false);
    }
  }

  pointsPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.pointsPageIndex = event.offset;
      this.addQueryParams({ p: this.pointsPageIndex });
      this.getStampEvents(true);
    }
  }

  redemptionPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.redemptionPageIndex = event.offset;
      this.addQueryParams({ p: this.redemptionPageIndex });
      this.getRedemptions();
    }
  }

  handleScroll() {
    this.suppressPaging = true;

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.suppressPaging = false;
    }, 100);
  }

  daysSelected(days: any) {
    this.days = days;
    this.addQueryParams({ days: this.days });
  }

  valueObtainedPercent(card: any): number {
    const stampCard =
      card?.card && typeof card.card !== 'string'
        ? (card.card?.[0] ?? card.card)
        : card.card;

    if (stampCard?.cardType == CardType.point) {
      const totalPoints = stampCard?.points?.totalPoints ?? 0;
      const currentPoints = card?.numberOfPoints ?? 0;
      return totalPoints ? (currentPoints / totalPoints) * 100 : 0;
    } else if (stampCard?.cardType == CardType.openEndedPoint) {
      const maxPoints = stampCard?.points?.maxPoints ?? 0;
      const currentPoints = card?.numberOfPoints ?? 0;

      if (maxPoints > 0) {
        return (currentPoints / maxPoints) * 100;
      } else {
        return 0;
      }
    } else {
      const totalStamps = stampCard?.numberOfStamps ?? 0;
      const currentStamps = card?.numberOfStamps ?? 0;
      return totalStamps ? (currentStamps / totalStamps) * 100 : 0;
    }
  }

  valueObtained(card: any): string {
    const stampCard =
      card?.card && typeof card.card !== 'string'
        ? (card.card?.[0] ?? card.card)
        : card.card;

    if (stampCard?.cardType === CardType.point) {
      const totalPoints = stampCard?.points?.totalPoints ?? 0;
      const currentPoints = card?.numberOfPoints ?? 0;

      return `${
        currentPoints > totalPoints ? totalPoints : currentPoints
      }/${totalPoints}`;
    } else if (stampCard?.cardType === CardType.openEndedPoint) {
      const maxPoints = stampCard?.points?.maxPoints ?? 0;
      const currentPoints = card?.numberOfPoints ?? 0;

      if (maxPoints > 0) {
        return `${currentPoints}/${maxPoints}`;
      } else {
        return `${currentPoints}`;
      }
    } else {
      const totalStamps = stampCard?.numberOfStamps ?? 0;
      const currentStamps = card?.numberOfStamps ?? 0;

      return `${
        currentStamps > totalStamps ? totalStamps : currentStamps
      }/${totalStamps}`;
    }
  }

  get showCardTypeColumn(): boolean {
    const hasDifferentTypes = !this.cards.every((card) => {
      if (Array.isArray(card.card) && Array.isArray(this.cards[0]?.card)) {
        return card.card?.[0]?.cardType === this.cards[0].card?.[0]?.cardType;
      } else {
        return false;
      }
    });

    return hasDifferentTypes;
  }

  cardType(card: any): string {
    const stampCard =
      card?.card && typeof card.card !== 'string'
        ? (card.card?.[0] ?? card.card)
        : card.card;

    return stampCard.cardType === CardType.stamp
      ? 'Stamp'
      : stampCard.cardType === CardType.point
        ? 'Point'
        : 'Point (Open Ended)';
  }

  rowTappedNGX({ row, type }: any): void {
    if (type === 'click') {
      const dialogRef = this.dialog.open(RedeemVoucherDialogComponent, {
        data: { voucherDetails: row },
        width: '700px',
        autoFocus: false,
        disableClose: true,
        scrollStrategy: new NoopScrollStrategy(),
        panelClass: 'custom-dialog',
      });

      dialogRef.afterClosed().subscribe((result: boolean) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  getPurchaserName(voucher: any): string {
    if (voucher?.source == 'merchant_issued') {
      return 'Merchant';
    }

    if (
      voucher?.purchaser &&
      voucher?.user &&
      voucher?.purchaser === voucher?.user?._id
    ) {
      return voucher?.user?.name;
    }

    if (voucher?.customer?.name) {
      return voucher?.customer?.name;
    }

    if (voucher?.other?.name) {
      return voucher?.other?.name;
    }

    return 'Non-customer';
  }
}
